import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon, CheckIcon, ClockIcon, SparklesIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import { useCurrentOrganization } from 'src/store/organization';
import { useHistory } from 'react-router-dom';
// import { apiMarkDiscountPopupShown } from 'src/utils/journeyApi';

interface TrialDiscountPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  daysSinceExpiry?: number;
}

export const TrialDiscountPopup: React.FC<TrialDiscountPopupProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  daysSinceExpiry = 0,
}) => {
  const { currentOrganization } = useCurrentOrganization();
  const history = useHistory();
  const [showDiscountCode, setShowDiscountCode] = useState(false);

  const handleUpgrade = () => {
    setShowDiscountCode(true);
    onUpgrade();
    history.push(`/${currentOrganization?.slug}/billing`);
  };

  const handleClaimDiscount = () => {
    setShowDiscountCode(!showDiscountCode);
  };

  const handleClose = () => {
    // Notify backend that popup was closed/shown
    // if (currentOrganization?.id) {
    //   apiMarkDiscountPopupShown(currentOrganization.id).catch(error => {
    //     console.warn('Failed to mark discount popup as shown:', error);
    //   });
    // }
    onClose();
  };

  const discountPercentage = 25; // 25% discount
  const originalPrice = 29; // Monthly price for Grow plan
  const discountedPrice = originalPrice * (1 - discountPercentage / 100);

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="fixed inset-0 overflow-y-auto z-[9999]" onClose={handleClose}>
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <SparklesIcon className="h-8 w-8 text-yellow-500" />
                  </div>
                  <div className="ml-3">
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      Special Offer for You! 🎉
                    </Dialog.Title>
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={handleClose}
                >
                  <span className="sr-only">Close</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              {/* Content */}
              <div className="mb-6">
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-2">
                    <ClockIcon className="h-5 w-5 text-yellow-600 mr-2" />
                    <span className="text-sm font-medium text-yellow-800">
                      {daysSinceExpiry} days since your trial ended
                    </span>
                  </div>
                  <p className="text-sm text-yellow-700">
                    We miss you! Here's a special discount to welcome you back to Journey.
                  </p>
                </div>

                <div className="text-center mb-6">
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">
                    Get {discountPercentage}% Off Forever
                  </h4>
                  <p className="text-gray-600 mb-4">
                    Upgrade to paid Journey Subscription to unlock all premium features
                  </p>
                  
                 
                </div>

                {/* Features */}
                <div className="mb-6 text-center">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">Here are some of our premium features for Journey:</h5>
                  <ul className="space-y-2">
                    {[
                      'Unlimited journeys and content',
                      'Advanced analytics and insights',
                      'Custom branding and themes',
                      'Email tracking and automation',
                      'Session Replays',
                      'AI-powered chatbot features',
                      'Custom NDA',
                      'Full Customization',
                      'Priority support'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                    onClick={handleClaimDiscount}
                  >
                    Claim {discountPercentage}% Discount
                  </button>
                </div>

                {/* Discount Code Section */}
                {showDiscountCode && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h6 className="text-sm font-medium text-green-800 mb-1">Your Discount Code:</h6>
                        <div className="flex items-center space-x-2">
                          <code className="text-lg font-mono font-bold text-green-900 bg-green-100 px-3 py-1 rounded">
                            JOURNEYSPECIAL25
                          </code>
                          <button
                            onClick={() => navigator.clipboard.writeText('JOURNEYSPECIAL25')}
                            className="text-green-600 hover:text-green-800 text-sm font-medium"
                          >
                            Copy
                          </button>
                        </div>
                        <p className="text-xs text-green-700 mt-2">
                          Use this code at checkout to get your {discountPercentage}% discount
                        </p>
                      </div>
                      <button
                        onClick={() => setShowDiscountCode(false)}
                        className="text-green-400 hover:text-green-600"
                      >
                        <XIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default TrialDiscountPopup;
